// #region IMPORTS
import {
  BufferGeometry,
  DynamicDrawUsage,
  Float32BufferAttribute,
  GLSL3,
  LineSegments,
  RawShaderMaterial,
} from 'three';

import { glColor } from '../util';

// #endregion

export default class DynLineMesh extends LineSegments<
  THREE.BufferGeometry,
  THREE.Material
> {
  // #region MAIN
  _defaultColor: number = 0x00ff00;
  _cnt: number = 0;
  _verts: Array<number> = [];
  _color: Array<number> = [];
  _config: Array<number> = [];
  _dirty: boolean = false;

  constructor(initSize = 20) {
    super(
      newDynLineMeshGeometry(
        new Float32Array(initSize * 2 * 3), // Two Points for Each Line
        new Float32Array(initSize * 2 * 3),
        new Float32Array(initSize * 2 * 1),
        false,
      ),
      new DynLineMeshMaterial(),
    );

    this.geometry.setDrawRange(0, 0);
    this.onBeforeRender = () => {
      if (this._dirty) this._updateGeometry();
    };
  }
  // #endregion

  // #region MISC
  reset() {
    this._cnt = 0;
    this._verts.length = 0;
    this._color.length = 0;
    this._config.length = 0;
    this.geometry.setDrawRange(0, 0);
    return this;
  }
  // #endregion

  // #region DRAWING
  push(
    p0: Array<number>,
    p1: Array<number>,
    color0: number = this._defaultColor,
    color1: number | null = null,
    isDash: boolean = false,
  ) {
    this._verts.push(p0[0], p0[1], p0[2], p1[0], p1[1], p1[2]);
    this._color.push(
      ...glColor(color0),
      ...glColor(color1 != null ? color1 : color0),
    );

    if (isDash) {
      const len = Math.sqrt(
        (p1[0] - p0[0]) ** 2 + (p1[1] - p0[1]) ** 2 + (p1[2] - p0[2]) ** 2,
      );
      this._config.push(0, len);
    } else {
      this._config.push(0, 0);
    }

    this._cnt++;
    this._dirty = true;
    return this;
  }

  box(
    v0: Array<number>,
    v1: Array<number>,
    col: number = this._defaultColor,
    is_dash: boolean = false,
  ) {
    const x1 = v0[0];
    const y1 = v0[1];
    const z1 = v0[2];
    const x2 = v1[0];
    const y2 = v1[1];
    const z2 = v1[2];

    this.push([x1, y1, z1], [x1, y1, z2], col, null, is_dash); // Bottom
    this.push([x1, y1, z2], [x2, y1, z2], col, null, is_dash);
    this.push([x2, y1, z2], [x2, y1, z1], col, null, is_dash);
    this.push([x2, y1, z1], [x1, y1, z1], col, null, is_dash);
    this.push([x1, y2, z1], [x1, y2, z2], col, null, is_dash); // Top
    this.push([x1, y2, z2], [x2, y2, z2], col, null, is_dash);
    this.push([x2, y2, z2], [x2, y2, z1], col, null, is_dash);
    this.push([x2, y2, z1], [x1, y2, z1], col, null, is_dash);
    this.push([x1, y1, z1], [x1, y2, z1], col, null, is_dash); // Sides
    this.push([x1, y1, z2], [x1, y2, z2], col, null, is_dash);
    this.push([x2, y1, z2], [x2, y2, z2], col, null, is_dash);
    this.push([x2, y1, z1], [x2, y2, z1], col, null, is_dash);
    return this;
  }

  circle(
    origin: Array<number>,
    xAxis: Array<number>,
    yAxis: Array<number>,
    radius: number,
    seg: number,
    col: number = this._defaultColor,
    is_dash: boolean = false,
  ) {
    const prevPos: Array<number> = [0, 0, 0];
    const pos: Array<number> = [0, 0, 0];
    const PI2 = Math.PI * 2;
    let rad = 0;

    planeCircle(origin, xAxis, yAxis, 0, radius, prevPos);
    for (let i = 1; i <= seg; i++) {
      rad = PI2 * (i / seg);
      planeCircle(origin, xAxis, yAxis, rad, radius, pos);
      this.push(prevPos, pos, col, null, is_dash);

      prevPos[0] = pos[0];
      prevPos[1] = pos[1];
      prevPos[2] = pos[2];
    }
  }
  // #endregion

  // #region PRIVATE
  _updateGeometry() {
    const geo = this.geometry;
    const bVerts = geo.attributes.position as Float32BufferAttribute;
    const bColor = geo.attributes.color as Float32BufferAttribute;
    const bConfig = geo.attributes.config as Float32BufferAttribute;

    //~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
    if (
      this._verts.length > bVerts.array.length ||
      this._color.length > bColor.array.length ||
      this._config.length > bConfig.array.length
    ) {
      if (this.geometry) this.geometry.dispose();
      this.geometry = newDynLineMeshGeometry(
        this._verts,
        this._color,
        this._config,
      );
      this._dirty = false;
      return;
    }

    //~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
    bVerts.array.set(this._verts);
    bVerts.count = this._verts.length / 3;
    bVerts.needsUpdate = true;

    bColor.array.set(this._color);
    bColor.count = this._color.length / 3;
    bColor.needsUpdate = true;

    bConfig.array.set(this._config);
    bConfig.count = this._config.length / 1;
    bConfig.needsUpdate = true;

    geo.setDrawRange(0, bVerts.count);
    geo.computeBoundingBox();
    geo.computeBoundingSphere();

    this._dirty = false;
  }
  // #endregion
}

// #region SUPPORT

function newDynLineMeshGeometry(
  aVerts: Float32Array | Array<number>,
  aColor: Float32Array | Array<number>,
  aConfig: Float32Array | Array<number>,
  doCompute: boolean = true,
): BufferGeometry {
  //~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
  const bVerts = new Float32BufferAttribute(aVerts, 3);
  const bColor = new Float32BufferAttribute(aColor, 3);
  const bConfig = new Float32BufferAttribute(aConfig, 1);
  bVerts.setUsage(DynamicDrawUsage);
  bColor.setUsage(DynamicDrawUsage);
  bConfig.setUsage(DynamicDrawUsage);

  //~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
  const geo = new BufferGeometry();
  geo.setAttribute('position', bVerts);
  geo.setAttribute('color', bColor);
  geo.setAttribute('config', bConfig);

  if (doCompute) {
    geo.computeBoundingSphere();
    geo.computeBoundingBox();
  }
  return geo;
}

function planeCircle(
  center: Array<number>,
  xAxis: Array<number>,
  yAxis: Array<number>,
  angle: number,
  radius: number,
  out: Array<number> = [0, 0, 0],
): Array<number> {
  const sin = Math.sin(angle);
  const cos = Math.cos(angle);
  out[0] = center[0] + radius * cos * xAxis[0] + radius * sin * yAxis[0];
  out[1] = center[1] + radius * cos * xAxis[1] + radius * sin * yAxis[1];
  out[2] = center[2] + radius * cos * xAxis[2] + radius * sin * yAxis[2];
  return out;
}

// #endregion

// #region MATERIAL

class DynLineMeshMaterial extends RawShaderMaterial {
  constructor() {
    super();

    // ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
    this.name = 'DynLineMeshMaterial';
    this.glslVersion = GLSL3;
    this.depthTest = true;
    this.transparent = true;

    this.uniforms = {
      dashSeg: { value: 1 / 0.07 },
      dashDiv: { value: 0.4 },
    };

    // ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
    this.vertexShader = `
    in vec3  position;
    in vec3  color;
    in float config;
    
    uniform mat4  modelViewMatrix;
    uniform mat4  projectionMatrix;
    uniform float u_scale;

    out vec3  fragColor;
    out float fragLen;
    
    void main(){
        vec4 vPos   = modelViewMatrix * vec4( position, 1.0 );
        gl_Position = projectionMatrix * vPos;

        fragColor   = color;
        fragLen     = config;
    }`;

    this.fragmentShader = `precision mediump float;
    uniform float dashSeg;
    uniform float dashDiv;

    in  vec3  fragColor;
    in  float fragLen;
    out vec4  outColor;

    void main(){
        float alpha = 1.0;
        if( fragLen > 0.0 ) alpha = step( dashDiv, fract( fragLen * dashSeg ) );
        outColor = vec4( fragColor, alpha );
    }`;
  }
}

// #endregion
